/**
 * The documents browser's state (#6559) and the pure functions around it: the
 * URL round-trip that makes a listing bookmarkable, the upload-range presets
 * computed in the viewer's zone, and the request the chips compile to.
 *
 * The state is owned by whoever mounts the browser: the page keeps it in the
 * URL, the picker keeps it in memory. Nothing here reads the DOM or the clock
 * on its own, so every rule is unit-testable against a fixed "now".
 */
import { Temporal } from "temporal-polyfill";
import {
  formatCalendarDay,
  instantFromWallClock,
  parseInstant,
  todayInZone,
} from "@/utils/temporal";
import type {
  ColumnFilterWire,
  DocumentRow,
  DocumentSortBy,
  DocumentSortOrder,
  DocumentStatusBucket,
  DocumentsSearchRequest,
  LinkedEntity,
} from "./api";
import { serviceKeyOf } from "./portals";
import { statusBucketOf } from "./rows";

/** Where the browser is looking. `home` is the landing view (Drive's Home:
 *  recent folders and documents); `all` browses the tree; `record` is a
 *  manager, fund, company or person as a location (round 5) — the documents
 *  linked to it, with `BrowserState.record` naming which; the rest are the
 *  named recursive views. */
export type BrowserView =
  | "home"
  | "all"
  | "record"
  | "records"
  | "recent"
  | "mine"
  | "locked"
  | "processing"
  /** The Shared drives listing (round 18): the shared drives as rows. */
  | "drives"
  /** Investor Portals (#11635 Round 4): the services overview above every
   *  document that arrived through a portal, grouped by manager. */
  | "portals";

/** What a bare URL means to the surface that owns the state: the page lands
 *  on Home, the picker browses the root. The default writes nothing back. */
export interface StateDefaults {
  view?: BrowserView;
}

export const VIEW_LABELS: Record<BrowserView, string> = {
  home: "Home",
  all: "Documents",
  record: "Record",
  records: "Records",
  recent: "Recent",
  mine: "My uploads",
  locked: "Locked",
  processing: "Processing",
  drives: "Shared drives",
  portals: "Investor Portals",
};

/** Whether an uploader key is a source rather than a person (#11635, spec
 *  R13): a connector's or service's `system:` key, or the one Finpilot a
 *  client organization sees, folded with the platform's system actions. */
export const isSourceKey = (key: string): boolean =>
  key.startsWith("system:") || key === "staff";

/** The uploader key that is every portal upload at once (spec R22). The
 *  portals view sends it unless a Source pick already narrows the rows. */
export const PORTALS_UPLOADER_KEY = "portals";

export type UploadedPreset = "today" | "7d" | "30d" | "year" | "custom";

export interface UploadedRange {
  preset: UploadedPreset;
  /** Calendar days, only read for the custom preset. */
  from?: string | null;
  to?: string | null;
}

export interface DocumentDateRange {
  from: string | null;
  to: string | null;
}

/** What kind of editor a custom column earns (#6559 round 8b): the column's
 *  data type folded to the six the chips know. */
export type ColumnKind =
  | "enum"
  | "boolean"
  | "date"
  | "number"
  | "entity"
  | "text";

/** One clause over a custom document column: the column, its kind (so the
 *  pill and the editor need no schema lookup), and the one operator that
 *  kind takes. `labels` are the display names behind record public ids. */
export interface ColumnClause {
  columnId: number;
  columnName: string;
  kind: ColumnKind;
  op: "in" | "is" | "between" | "contains";
  values?: string[];
  labels?: string[];
  /** The kinds behind record public ids, so a reopened editor names them. */
  kinds?: string[];
  value?: boolean;
  from?: string | null;
  to?: string | null;
  text?: string;
}

/** Where a search looks (round 18, Hunter, Drive's Location menu): a search
 *  looks everywhere unless the reader narrows it — to a place (a drive, a
 *  connection's root, or a folder inside one), to every shared drive, or to
 *  the connected apps' roots. Null is anywhere. */
export type LocationFilter =
  | { kind: "place"; driveId: string | null; path: string[] }
  | { kind: "shared" }
  | { kind: "apps" };

export interface DocumentFilters {
  types: string[];
  uploaders: string[];
  uploaded: UploadedRange | null;
  documentDate: DocumentDateRange | null;
  status: DocumentStatusBucket[];
  entities: LinkedEntity[];
  /** Custom attribute clauses, ANDed (round 8b). */
  columns: ColumnClause[];
  /** The Location chip (round 18); null is anywhere. */
  location: LocationFilter | null;
}

/** One sync's window (#11635 slice 1, Round 15, R51): the run's start and
 *  end as the runs route sends them (wire instants), `to` null for a run
 *  still open. */
export interface RunWindow {
  from: string;
  to: string | null;
}

export interface BrowserSort {
  by: DocumentSortBy;
  order: DocumentSortOrder;
}

export interface BrowserState {
  path: string[];
  view: BrowserView;
  /** The record's public id when `view` is `record`; the page resolves its
   *  kind and name from it, so a link needs nothing else. Null otherwise. */
  record: string | null;
  filters: DocumentFilters;
  q: string;
  /** The sort the user chose, or null for the view's default. A choice and
   *  "no choice" must stay distinct: a default that happened to equal a
   *  legitimate pick made that pick unreachable in the named views. */
  sort: BrowserSort | null;
  /** A document to point at on arrival: "Show in folder" lands in a folder
   *  that may hold hundreds of rows, and a listing that does not say which
   *  one you asked about has answered a different question (Hunter, round
   *  7g). It rides in the URL so Back retraces to the same row. */
  focus: string | null;
  /** The drive the listing is in (round 17), for an organization with
   *  drives on; null means the page's default place (My Drive). It rides in
   *  the URL like the folder, since a folder path names nothing without its
   *  drive and Back must retrace a change of drive. */
  drive: string | null;
  /** The investor portal service the portals view is inside (#11635 slice
   *  1, Round 7, R21), by its name; null on the services overview. A
   *  service is a folder, not a filter: opening one clears the chips, and
   *  it rides in the URL as `svc` so Back retraces it. Null outside the
   *  portals view. */
  service: string | null;
  /** The sync whose documents the service's Documents section is narrowed
   *  to (Round 15, R49, R51); null when none is. Only inside a service: it
   *  rides in the URL as `run` beside `svc`, and every way out of the
   *  service clears it. */
  run: RunWindow | null;
}

export const EMPTY_FILTERS: DocumentFilters = {
  types: [],
  uploaders: [],
  uploaded: null,
  documentDate: null,
  status: [],
  entities: [],
  columns: [],
  location: null,
};

export const DEFAULT_SORT: BrowserSort = { by: "name", order: "asc" };

export const DEFAULT_BROWSER_STATE: BrowserState = {
  path: [],
  view: "all",
  record: null,
  service: null,
  run: null,
  filters: EMPTY_FILTERS,
  q: "",
  sort: null,
  focus: null,
  drive: null,
};

const VIEWS: readonly BrowserView[] = [
  "home",
  "all",
  "record",
  "records",
  "recent",
  "mine",
  "locked",
  "processing",
  "drives",
  "portals",
];
const STATUSES: readonly DocumentStatusBucket[] = [
  "processing",
  "completed",
  "failed",
  "locked",
];
const PRESETS: readonly UploadedPreset[] = [
  "today",
  "7d",
  "30d",
  "year",
  "custom",
];
const SORT_KEYS: readonly DocumentSortBy[] = [
  "name",
  "uploaded_at",
  "document_date",
];

const isOneOf = <T extends string>(
  values: readonly T[],
  value: string | null
): value is T =>
  value !== null && (values as readonly string[]).includes(value);

export const hasActiveFilterValues = (f: DocumentFilters): boolean =>
  f.types.length > 0 ||
  f.uploaders.length > 0 ||
  f.uploaded !== null ||
  f.documentDate !== null ||
  f.status.length > 0 ||
  f.entities.length > 0 ||
  f.columns.length > 0 ||
  f.location !== null;

export const hasActiveFilters = (state: BrowserState): boolean =>
  hasActiveFilterValues(state.filters) || state.q.trim() !== "";

/** Whether the listing searches beneath its folder rather than listing it.
 *  Any filter or search term, and every named view, does. */
export const isRecursive = (state: BrowserState): boolean =>
  state.view !== "all" || hasActiveFilters(state);

/** Opening a folder means seeing the folder: the search term and the chips
 *  go, or the destination would render as "Results in …" — from a row, the
 *  tree, "Show in folder" or the details pane alike. */
export const openedFolder = (
  state: BrowserState,
  path: string[],
  focus: string | null = null
): BrowserState => ({
  ...state,
  view: "all",
  record: null,
  service: null,
  run: null,
  path,
  q: "",
  filters: EMPTY_FILTERS,
  focus,
});

/** Opening an investor portal service (or, with null, the services
 *  overview) is arriving in a folder (Round 7, R21): the chips and the
 *  search term go, whether from a tile or the sidebar. */
export const openedService = (
  state: BrowserState,
  service: string | null
): BrowserState => ({
  ...state,
  view: "portals",
  record: null,
  service,
  run: null,
  drive: null,
  path: [],
  q: "",
  filters: EMPTY_FILTERS,
  focus: null,
});

/** Opening a record is arriving somewhere too: the folder, the search term
 *  and the chips go, and the record's documents are the listing. */
export const openedRecord = (
  state: BrowserState,
  publicId: string
): BrowserState => ({
  ...state,
  view: "record",
  record: publicId,
  service: null,
  run: null,
  path: [],
  q: "",
  filters: EMPTY_FILTERS,
  focus: null,
});

/** A service's listing apart from the sync it is narrowed to (#11635 slice
 *  1, Round 15, R52), as a key: everything the state says but the run.
 *  Null outside a service, where no run narrows anything. */
export const listingBesideTheRun = (state: BrowserState): string | null =>
  state.view === "portals" && state.service !== null
    ? JSON.stringify({ ...state, run: null })
    : null;

/** Whether the rows on screen may stand in while the next listing loads
 *  (R52): only when the two differ in the sync's narrowing alone (applied,
 *  moved or cleared inside one service). A chip, a term, a folder or a view
 *  change shows the skeleton as before. Read over the two listings'
 *  `listingBesideTheRun` keys, which is what the search query keeps. */
export const keepsRowsAcross = (
  previous: string | null,
  next: string | null
): boolean => next !== null && previous === next;

/** The named views list from the drive root; browsing lists the open folder. */
export const effectivePath = (state: BrowserState): string[] =>
  state.view === "all" ? state.path : [];

// --- URL state ---------------------------------------------------------------

/**
 * Serialize to query parameters. The default state writes nothing, so the
 * page's URL stays bare until the user does something. A folder path is one
 * repeated `p` parameter per segment, never a joined string, so a segment with
 * a slash in it survives.
 */
export const toSearchParams = (
  state: BrowserState,
  defaults: StateDefaults = {}
): URLSearchParams => {
  const params = new URLSearchParams();
  for (const segment of state.path) params.append("p", segment);
  if (state.view !== (defaults.view ?? "all")) params.set("view", state.view);
  if (state.view === "record" && state.record)
    params.set("record", state.record);
  if (state.view === "portals" && state.service)
    params.set("svc", state.service);
  if (state.view === "portals" && state.service && state.run)
    params.set("run", `${state.run.from}..${state.run.to ?? ""}`);
  for (const type of state.filters.types) params.append("type", type);
  for (const key of state.filters.uploaders) params.append("by", key);
  const uploaded = state.filters.uploaded;
  if (uploaded) {
    params.set(
      "uploaded",
      uploaded.preset === "custom"
        ? `custom:${uploaded.from ?? ""}..${uploaded.to ?? ""}`
        : uploaded.preset
    );
  }
  const docDate = state.filters.documentDate;
  if (docDate)
    params.set("docdate", `${docDate.from ?? ""}..${docDate.to ?? ""}`);
  for (const status of state.filters.status) params.append("status", status);
  if (state.filters.columns.length > 0) {
    params.set("attr", JSON.stringify(state.filters.columns));
  }
  if (state.filters.entities.length > 0) {
    params.set("entities", JSON.stringify(state.filters.entities));
  }
  if (state.filters.location)
    params.set("loc", JSON.stringify(state.filters.location));
  if (state.q) params.set("q", state.q);
  if (state.sort) params.set("sort", `${state.sort.by}.${state.sort.order}`);
  if (state.focus) params.set("focus", state.focus);
  if (state.drive) params.set("d", state.drive);
  return params;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayOrNull = (value: string | undefined): string | null =>
  value && DAY.test(value) ? value : null;

const parseRange = (raw: string): DocumentDateRange => {
  const [from, to] = raw.split("..");
  return { from: dayOrNull(from), to: dayOrNull(to) };
};

/** A sync's window read back from the URL (R51): `<from>..<to>`, both wire
 *  instants, `to` empty for an open run; anything else is null. */
const parseRun = (raw: string | null): RunWindow | null => {
  if (!raw) return null;
  const parts = raw.split("..");
  if (parts.length !== 2) return null;
  const [from, to] = parts as [string, string];
  if (!parseInstant(from)) return null;
  if (to !== "" && !parseInstant(to)) return null;
  return { from, to: to === "" ? null : to };
};

const COLUMN_KINDS: readonly ColumnKind[] = [
  "enum",
  "boolean",
  "date",
  "number",
  "entity",
  "text",
];
const CLAUSE_OPS: readonly ColumnClause["op"][] = [
  "in",
  "is",
  "between",
  "contains",
];

const asStringList = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((v) => typeof v === "string")
    ? (value as string[])
    : null;

/** A clause read back from the URL, or null when it is not one. A clause is
 *  kept only when it says everything its operator needs, so a stale or
 *  hand-edited URL narrows nothing silently. */
const parseClause = (raw: unknown): ColumnClause | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (typeof c.columnId !== "number" || typeof c.columnName !== "string")
    return null;
  if (typeof c.kind !== "string" || !isOneOf(COLUMN_KINDS, c.kind)) return null;
  if (typeof c.op !== "string" || !isOneOf(CLAUSE_OPS, c.op)) return null;
  const base = { columnId: c.columnId, columnName: c.columnName, kind: c.kind };
  switch (c.op) {
    case "in": {
      const values = asStringList(c.values);
      if (!values || values.length === 0) return null;
      const labels = asStringList(c.labels);
      const kinds = asStringList(c.kinds);
      return {
        ...base,
        op: "in",
        values,
        ...(labels ? { labels } : {}),
        ...(kinds ? { kinds } : {}),
      };
    }
    case "is":
      return typeof c.value === "boolean"
        ? { ...base, op: "is", value: c.value }
        : null;
    case "between": {
      const from = typeof c.from === "string" ? c.from : null;
      const to = typeof c.to === "string" ? c.to : null;
      return from === null && to === null
        ? null
        : { ...base, op: "between", from, to };
    }
    case "contains":
      return typeof c.text === "string" && c.text.trim() !== ""
        ? { ...base, op: "contains", text: c.text }
        : null;
  }
};

const parseClauses = (raw: string | null): ColumnClause[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(parseClause).filter((c): c is ColumnClause => c !== null);
  } catch {
    return [];
  }
};

const summarizeList = (values: string[]): string =>
  values.length <= 2
    ? values.join(", ")
    : `${values.slice(0, 2).join(", ")} +${values.length - 2}`;

const dayLabel = (day: string): string => formatCalendarDay(day, "medium");

/** The pill's label for a clause: what it narrows to, in the words the
 *  editor used. */
export const clauseSummary = (clause: ColumnClause): string => {
  switch (clause.op) {
    case "in":
      return summarizeList(clause.labels ?? clause.values ?? []);
    case "is":
      return clause.value ? "Yes" : "No";
    case "between": {
      const from = clause.from ?? null;
      const to = clause.to ?? null;
      if (clause.kind === "date") {
        if (from && to) return `${dayLabel(from)} – ${dayLabel(to)}`;
        if (from) return `from ${dayLabel(from)}`;
        return to ? `to ${dayLabel(to)}` : "";
      }
      if (from && to) return `${from} – ${to}`;
      if (from) return `≥ ${from}`;
      return to ? `≤ ${to}` : "";
    }
    case "contains":
      return `contains “${clause.text ?? ""}”`;
  }
};

/** The clause on the wire: the column and the operator's operands, nothing
 *  the server would have to ignore. */
export const clauseToWire = (clause: ColumnClause): ColumnFilterWire => {
  switch (clause.op) {
    case "in":
      return {
        column_id: clause.columnId,
        op: "in",
        values: clause.values ?? [],
      };
    case "is":
      return {
        column_id: clause.columnId,
        op: "is",
        value: clause.value ?? false,
      };
    case "between":
      return {
        column_id: clause.columnId,
        op: "between",
        from: clause.from ?? null,
        to: clause.to ?? null,
      };
    case "contains":
      return {
        column_id: clause.columnId,
        op: "contains",
        text: clause.text ?? "",
      };
  }
};

const parseLocation = (raw: string | null): LocationFilter | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { kind, driveId, path } = parsed as Record<string, unknown>;
    if (kind === "shared" || kind === "apps") return { kind };
    if (
      kind === "place" &&
      (typeof driveId === "string" || driveId === null) &&
      Array.isArray(path) &&
      path.every((segment) => typeof segment === "string")
    )
      return { kind, driveId, path: path as string[] };
    return null;
  } catch {
    return null;
  }
};

const parseEntities = (raw: string | null): LinkedEntity[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((e: unknown) => {
      if (typeof e !== "object" || e === null) return [];
      const { kind, publicId, name } = e as Record<string, unknown>;
      if (
        typeof kind !== "string" ||
        typeof publicId !== "string" ||
        typeof name !== "string"
      )
        return [];
      return [{ kind, publicId, name }];
    });
  } catch {
    return [];
  }
};

/** The inverse of `toSearchParams`. Anything unrecognised is dropped rather
 *  than thrown, so a hand-edited or stale URL still opens the page. */
export const fromSearchParams = (
  params: URLSearchParams,
  defaults: StateDefaults = {}
): BrowserState => {
  const view = params.get("view");
  const uploadedRaw = params.get("uploaded");
  let uploaded: UploadedRange | null = null;
  if (uploadedRaw) {
    if (uploadedRaw.startsWith("custom:")) {
      const range = parseRange(uploadedRaw.slice("custom:".length));
      uploaded = { preset: "custom", from: range.from, to: range.to };
    } else if (isOneOf(PRESETS, uploadedRaw) && uploadedRaw !== "custom") {
      uploaded = { preset: uploadedRaw };
    }
  }
  const docDateRaw = params.get("docdate");
  const documentDate = docDateRaw ? parseRange(docDateRaw) : null;
  const sortRaw = params.get("sort");
  let sort: BrowserSort | null = null;
  if (sortRaw) {
    const [by, order] = sortRaw.split(".");
    if (isOneOf(SORT_KEYS, by) && (order === "asc" || order === "desc")) {
      sort = { by, order };
    }
  }
  const record = params.get("record");
  // A record view is only a view of something: without the id it is nothing.
  const resolvedView: BrowserView =
    isOneOf(VIEWS, view) && (view !== "record" || record)
      ? view
      : (defaults.view ?? "all");
  const service = resolvedView === "portals" ? params.get("svc") : null;
  return {
    path: params.getAll("p"),
    view: resolvedView,
    record: resolvedView === "record" ? record : null,
    service,
    run: service ? parseRun(params.get("run")) : null,
    filters: {
      types: params.getAll("type"),
      // A service is a folder and its Source chip stands down (Round 7,
      // R21): a `by` beside `svc` would narrow with no chip to clear it.
      uploaders: service ? [] : params.getAll("by"),
      uploaded,
      documentDate:
        documentDate && (documentDate.from || documentDate.to)
          ? documentDate
          : null,
      // Failed is not a filter users are offered (round 18i): a link
      // carrying it reads as no status filter.
      status: params
        .getAll("status")
        .filter((s) => isOneOf(STATUSES, s) && s !== "failed"),
      entities: parseEntities(params.get("entities")),
      columns: parseClauses(params.get("attr")),
      location: parseLocation(params.get("loc")),
    },
    q: params.get("q") ?? "",
    sort,
    focus: params.get("focus"),
    drive: params.get("d"),
  };
};

// --- upload presets ----------------------------------------------------------

const daysBefore = (day: string, count: number): string =>
  Temporal.PlainDate.from(day).subtract({ days: count }).toString();

const nextDay = (day: string): string | null => {
  // A calendar step, so it stays a calendar date: no midnight, no zone.
  try {
    return Temporal.PlainDate.from(day).add({ days: 1 }).toString();
  } catch {
    return null;
  }
};

/**
 * The instants an upload-range preset bounds, in the viewer's zone. "Last 7
 * days" starts seven viewer days ago at the viewer's midnight, "this year" on
 * the viewer's January 1st, and a custom range covers both of its days in
 * full. `before` is exclusive, so a range ending on a day ends at the start of
 * the next one.
 */
export const uploadedBounds = (
  range: UploadedRange,
  now: Date,
  zone: string
): { after: string | undefined; before: string | undefined } => {
  const today = todayInZone(zone, now);
  const startOf = (day: string | null): string | undefined =>
    day ? (instantFromWallClock(day, "00:00", zone) ?? undefined) : undefined;
  switch (range.preset) {
    case "today":
      return { after: startOf(today), before: undefined };
    case "7d":
      return { after: startOf(daysBefore(today, 7)), before: undefined };
    case "30d":
      return { after: startOf(daysBefore(today, 30)), before: undefined };
    case "year":
      return {
        after: startOf(`${today.slice(0, 4)}-01-01`),
        before: undefined,
      };
    case "custom": {
      const to = range.to ? nextDay(range.to) : null;
      return { after: startOf(range.from ?? null), before: startOf(to) };
    }
  }
};

// --- chips over a row ---------------------------------------------------------

/**
 * Whether one row passes the chips — the client-side reading of the same
 * rules the search route applies, for a list that did not come from the
 * route with the chips attached (Home's suggestions). `viewerKeys` are the
 * uploader keys that mean the viewer (`me`, `user:<id>`).
 *
 * Not a custom attribute clause: a row carries no cell values, so it cannot
 * be judged here, and Home yields to the listing whenever one is active
 * (DocumentsBrowser). Keep that guard if this ever grows a `columns` arm.
 */
export const matchesFilters = (
  row: DocumentRow,
  filters: DocumentFilters,
  options: { viewerKeys: string[]; now: Date; zone: string }
): boolean => {
  const f = filters;
  if (f.types.length && !row.documentTypes.some((t) => f.types.includes(t)))
    return false;
  if (f.uploaders.length) {
    const key = row.uploadedBy?.key;
    // The staff key is the one Finpilot a client organization sees: the
    // masked staff uploads and the platform's own system actions, as the
    // route folds them (round 19f).
    const wanted = f.uploaders.flatMap((k) =>
      k === "me"
        ? options.viewerKeys
        : k === "staff"
          ? ["staff", "system:Finpilot"]
          : [k]
    );
    if (!key || !wanted.includes(key)) return false;
  }
  if (f.uploaded) {
    const { after, before } = uploadedBounds(
      f.uploaded,
      options.now,
      options.zone
    );
    if (!row.uploadedAt) return false;
    if (after && row.uploadedAt < after) return false;
    if (before && row.uploadedAt >= before) return false;
  }
  if (f.documentDate) {
    if (!row.documentDate) return false;
    if (f.documentDate.from && row.documentDate < f.documentDate.from)
      return false;
    if (f.documentDate.to && row.documentDate > f.documentDate.to) return false;
  }
  if (f.status.length && !f.status.includes(statusBucketOf(row))) return false;
  if (
    f.entities.length &&
    !row.linkedEntities.some((e) =>
      f.entities.some((w) => w.publicId === e.publicId)
    )
  )
    return false;
  return true;
};

// --- request -----------------------------------------------------------------

/** Where a narrowed browse listing looks (round 17, Claude Design): the
 *  folder, the place, every drive, or the connections' roots. Applied only
 *  to a browse listing that is narrowed by a term or a chip; the named
 *  views keep their own reach. */
export interface SearchScopeRequest {
  path: string[];
  /** The drive, or null for every drive. */
  driveId: string | null;
  /** The connected apps' roots, each in its drive. */
  roots?: Array<{ driveId: string | null; name: string }>;
  /** Every shared drive (round 18). */
  driveIds?: string[];
}

export interface RequestOptions {
  now: Date;
  zone: string;
  driveId?: string | null;
  scope?: SearchScopeRequest;
  excludeBlobKinds?: string[];
  pageSize?: number;
  cursor?: string | null;
  /** A fixed set of documents — the picker's "Show selected" (round 10):
   *  listed wherever they are, so the listing is recursive by definition. */
  blobIds?: string[];
  /** False asks for no total (#12073): a listing that shows none should not
   *  pay for an organization-wide count. Absent, the total comes as before. */
  withTotal?: boolean;
}

/** Recent is the last 30 days, capped: 9,709 rows sorted by upload date is an
 *  archive, not a recency view, and in a picker it is unnavigable (Claude
 *  Design, round 8). The window is the request's; the cap is the listing's. */
export const RECENT_DAYS = 30;
export const RECENT_DAYS_PRESET: UploadedPreset = "30d";
export const RECENT_CAP = 200;

/** Whether a listing has reached its cap and must stop paging. */
export const listingCapped = (state: BrowserState, rowCount: number): boolean =>
  state.view === "recent" && rowCount >= RECENT_CAP;

const VIEW_SORT: Record<BrowserView, BrowserSort | null> = {
  home: { by: "uploaded_at", order: "desc" },
  all: null,
  record: { by: "uploaded_at", order: "desc" },
  // The index is a list of records, not of documents; it sorts itself.
  records: null,
  recent: { by: "uploaded_at", order: "desc" },
  processing: { by: "uploaded_at", order: "desc" },
  mine: { by: "uploaded_at", order: "desc" },
  locked: { by: "uploaded_at", order: "desc" },
  // Places listings, not documents; a term on one is results everywhere.
  drives: { by: "uploaded_at", order: "desc" },
  // Newest arrival first: the view is a check on what came in.
  portals: { by: "uploaded_at", order: "desc" },
};

/** The sort a listing actually runs: the user's explicit choice, else the
 *  view's default (Recent is newest-first; browsing is by name). */
export const effectiveSort = (state: BrowserState): BrowserSort =>
  state.sort ?? VIEW_SORT[state.view] ?? DEFAULT_SORT;

const epochMsOf = (at: string): number =>
  parseInstant(at)?.epochMilliseconds ?? Number.NaN;

/** The later of two optional instants, as given (either may be absent). */
const laterInstant = (
  a: string | undefined,
  b: string | undefined
): string | undefined =>
  a === undefined
    ? b
    : b === undefined
      ? a
      : epochMsOf(b) > epochMsOf(a)
        ? b
        : a;

/** The earlier of two optional instants, as given. */
const earlierInstant = (
  a: string | undefined,
  b: string | undefined
): string | undefined =>
  a === undefined
    ? b
    : b === undefined
      ? a
      : epochMsOf(b) < epochMsOf(a)
        ? b
        : a;

export const buildSearchRequest = (
  state: BrowserState,
  options: RequestOptions
): DocumentsSearchRequest => {
  const f = state.filters;
  // Recent carries its own window; an Uploaded chip of the user's replaces
  // it rather than intersecting with it.
  const chip = f.uploaded
    ? uploadedBounds(f.uploaded, options.now, options.zone)
    : state.view === "recent"
      ? uploadedBounds(
          { preset: RECENT_DAYS_PRESET },
          options.now,
          options.zone
        )
      : { after: undefined, before: undefined };
  // A sync's window (Round 15, R51), inside a service only, intersected
  // with the chip's bounds: the later start, the earlier end.
  const run = state.view === "portals" && state.service ? state.run : null;
  const uploaded = run
    ? {
        after: laterInstant(chip.after, run.from),
        before: earlierInstant(chip.before, run.to ?? undefined),
      }
    : chip;
  const sort = effectiveSort(state);
  const pageSize = options.pageSize ?? 100;
  // A fixed set of ids is listed as itself (round 10, "Show selected"):
  // wherever the documents are, whatever chips, term or view the reader
  // was in — none of those narrow the picks, and neither does the
  // selection surface's own "no failures" status (a failed document was
  // never listed, so it was never picked).
  if (options.blobIds) {
    const picks: DocumentsSearchRequest = {
      path: [],
      recursive: true,
      sort_by: sort.by,
      sort_order: sort.order,
      page_size: pageSize,
      blob_ids: options.blobIds,
    };
    if (options.driveId) picks.drive_id = options.driveId;
    if (options.excludeBlobKinds?.length)
      picks.exclude_blob_kinds = options.excludeBlobKinds;
    if (options.cursor) picks.cursor = options.cursor;
    if (options.withTotal === false) picks.with_total = false;
    return picks;
  }
  // The portals view is every portal upload (spec R22) until a Source pick
  // names the services; a pick is already a subset, so the key stands down.
  // A people key can only reach the view through a URL (the chip is hidden
  // there), and it would widen the listing: it goes with the chip.
  // Inside a service (Round 7, R22) the listing is that service's.
  const uploaders =
    state.view === "portals" && state.service
      ? [serviceKeyOf(state.service)]
      : state.view === "portals"
        ? f.uploaders.some(isSourceKey)
          ? f.uploaders.filter(isSourceKey)
          : [PORTALS_UPLOADER_KEY]
        : [...f.uploaders];
  const status = [...f.status];
  if (state.view === "mine" && !uploaders.includes("me")) uploaders.push("me");
  if (state.view === "locked" && !status.includes("locked"))
    status.push("locked");
  if (state.view === "processing" && !status.includes("processing"))
    status.push("processing");
  // A plain folder listing is the folder. Anything recursive — a term, a
  // chip, a named view — looks where the scope says (round 18, Hunter,
  // Drive's model: everywhere unless the Location chip narrows it), except
  // a record location, which is the record's documents wherever they are —
  // in no drive in particular, as the index's count and the RMS tab have
  // it (round 19d). A caller that gives no scope (the picker) keeps the
  // listing's drive for everything else.
  const onRecord = state.view === "record";
  const scope =
    options.scope && !onRecord && isRecursive(state) ? options.scope : null;
  const request: DocumentsSearchRequest = {
    path: scope ? scope.path : effectivePath(state),
    recursive: isRecursive(state),
    sort_by: sort.by,
    sort_order: sort.order,
    page_size: pageSize,
  };
  if (scope) {
    if (scope.driveId) request.drive_id = scope.driveId;
    if (scope.roots?.length)
      request.roots = scope.roots.map((root) => ({
        drive_id: root.driveId,
        name: root.name,
      }));
    if (scope.driveIds?.length) request.drive_ids = scope.driveIds;
  } else if (options.driveId && !onRecord) request.drive_id = options.driveId;
  if (options.excludeBlobKinds?.length)
    request.exclude_blob_kinds = options.excludeBlobKinds;
  if (options.cursor) request.cursor = options.cursor;
  if (options.withTotal === false) request.with_total = false;
  if (state.q.trim()) request.q = state.q.trim();
  if (f.types.length) request.document_types = f.types;
  if (uploaders.length) request.uploaders = uploaders;
  if (uploaded.after) request.uploaded_after = uploaded.after;
  if (uploaded.before) request.uploaded_before = uploaded.before;
  if (f.documentDate?.from) request.document_date_from = f.documentDate.from;
  if (f.documentDate?.to) request.document_date_to = f.documentDate.to;
  if (status.length) request.status = status;
  if (state.view === "record" && state.record) {
    // The record is the listing; the Related to chip stands down here.
    request.entity_public_ids = [state.record];
  } else if (f.entities.length) {
    request.entity_public_ids = f.entities.map((e) => e.publicId);
  }
  if (f.columns.length) request.column_filters = f.columns.map(clauseToWire);
  return request;
};
